<template>
  <section class="message__content" :class="messageTypeClass">
    <div
      v-if="shouldShowParticipantName && participantName"
      class="message__content__info message__user__name"
    >
      {{ participantName }}
    </div>
    <div v-if="message.replyTo" class="message__content--reply">
      <div class="message__content--reply__sender">
        {{ replySender.participantName }}
      </div>
      <component
        v-bind="replyComponent.props"
        :is="replyComponent.component"
        class="message__content--reply__component"
      />
    </div>

    <div class="message__group">
      <component
        v-bind="componentProps"
        :is="component"
        @play="onPlayAudio"
        @internal-redirect="onInternalRedirect"
      />
      <slot></slot>
      <div
        v-if="message.createdAt && showTime"
        class="message__content__info message__time"
      >
        {{ formattedCreatedAt }}
      </div>
    </div>
  </section>
</template>
<script>
import {
  CMessageAnswer,
  CMessageAudio,
  CMessageDocument,
  CMessageImage,
  CMessageInternalRedirect,
  CMessageQuestion,
  CMessageText,
  CMessageVideo
} from '@/components/channels/atoms'
import { MessageTypes } from '@/models/channels'
import time from '@/utils/time'

export default {
  components: {
    CMessageAudio,
    CMessageDocument,
    CMessageImage,
    CMessageText,
    CMessageVideo,
    CMessageQuestion,
    CMessageAnswer,
    CMessageInternalRedirect
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    component: { type: String, default: undefined },
    componentProps: { type: Object, default: undefined },
    previousMessage: { type: Object, default: undefined },
    nextMessage: { type: Object, default: undefined },
    onPlayAudio: {
      type: Function,
      default: () => undefined
    },
    messageSender: { type: Object, default: undefined },
    replySender: { type: Object, default: undefined },
    showTime: Boolean,
    replyComponent: { type: Object, default: undefined }
  },
  emits: ['onInternalRedirect'],
  computed: {
    shouldShowParticipantName() {
      const hasNoPreviousMessage = !this.previousMessage
      const isFromDifferentUsers =
        this.message.userId !== this.previousMessage?.userId

      if (hasNoPreviousMessage || isFromDifferentUsers) {
        return true
      }
      const currentMessageAndPreviousMessageHasTime =
        this.message?.createdAt?.seconds &&
        this.previousMessage?.createdAt?.seconds

      if (currentMessageAndPreviousMessageHasTime) {
        const differentDate =
          time(this.message.createdAt.seconds) !==
          time(this.previousMessage.createdAt.seconds)
        return differentDate
      }
      return false
    },
    participantName() {
      return this.messageSender?.participantName
    },
    isImageType() {
      return this.message.type === MessageTypes.IMAGE
    },
    isAudioType() {
      return this.message.type === MessageTypes.AUDIO
    },
    isVideoType() {
      return this.message.type === MessageTypes.VIDEO
    },
    isDocumentType() {
      return this.message.type === MessageTypes.DOCUMENT
    },
    messageTypeClass() {
      return {
        'message__content--image': this.isImageType,
        'message__content--audio': this.isAudioType,
        'message__content--video': this.isVideoType,
        'message__content--document': this.isDocumentType
      }
    },
    formattedCreatedAt() {
      return time(this.message.createdAt)
    }
  },
  methods: {
    onInternalRedirect(data) {
      this.$emit('onInternalRedirect', data)
    }
  }
}
</script>
<style lang="scss">
:root,
[data-theme='on-light'] {
  --message-content-info-color: var(--sys-color-content-tertiary);
}

.message__content {
  position: relative;
  padding: var(--gl-spacing-02);
  padding-left: var(--gl-spacing-03);

  .message__group {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }

  &__info {
    font-size: var(--gl-font-size-xxxs);
    color: var(--message-content-info-color);
    line-height: 12px;

    &.message__time {
      font-weight: var(--gl-font-weight-regular);
      padding-left: var(--gl-spacing-03);
      padding-top: var(--gl-spacing-02);
    }

    &.message__user__name {
      font-weight: var(--gl-font-weight-semibold);
      margin-right: var(--gl-spacing-12);
      margin-bottom: var(--gl-spacing-02);
    }
  }

  &--video,
  &--document {
    padding: var(--gl-spacing-01);

    .message__group {
      flex-direction: column;
    }

    .message__user__name {
      padding-top: var(--gl-spacing-01);
      padding-left: var(--gl-spacing-02);
    }
    .message__time {
      padding-bottom: var(--gl-spacing-01);
      padding-right: var(--gl-spacing-02);
    }
  }

  &--image {
    @extend .message__content--video;
    .message__time {
      position: absolute;
      bottom: var(--gl-spacing-01);
      padding: var(--gl-spacing-02);
      color: var(--gl-color-shades-gray-10);
      border-bottom-right-radius: var(--gl-border-radius-md);
      height: calc(100% / 4);
      width: calc(100% / 3);
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      background-image: linear-gradient(to top left, #000 10%, transparent 55%);
    }
  }

  &--audio {
    .message__time {
      position: absolute;
      right: var(--gl-spacing-02);
      bottom: var(--gl-spacing-02);
    }
  }

  &--reply {
    background: var(--gl-color-shades-gray-10);
    border-left: var(--gl-border-width-sm) solid var(--gl-color-shades-gray-20);
    border-radius: var(--gl-border-radius-sm);
    padding: var(--gl-spacing-02);
    margin-bottom: var(--gl-spacing-01);
    font-size: var(--gl-font-size-xxs);
    font-weight: var(--gl-font-weight-regular);
    &__component {
      margin-top: var(--gl-spacing-01);
    }
    color: var(--gl-color-shades-gray-70);
  }
  .message__sender--other &--reply {
    background: var(--gl-color-shades-white);
  }
  .message__private &--reply {
    background: var(--gl-color-shades-gray-90);
    border-left: var(--gl-border-width-sm) solid var(--gl-color-shades-gray-90);
    color: var(--gl-color-shades-gray-30);
  }
  .message__sender--member &--reply {
    background: var(--gl-color-shades-magenta-80);
    border-color: var(--gl-color-shades-magenta-100);
    &__sender {
      color: var(--gl-color-shades-gray-10);
    }
  }
}
</style>
